import React, { memo, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { IoLogoInstagram } from "react-icons/io5";
import { FaFacebookSquare, FaTelegramPlane } from "react-icons/fa";
import logo from "assets/layout_img/logo.svg";
import "styles/public.css";

const Footer = memo(() => {
  const currentYear = new Date().getFullYear();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const scrollToSection = (e, selector) => {
    e.preventDefault();
    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const socialLinks = [
    {
      href: "https://www.instagram.com/algoritm.academy/",
      icon: <IoLogoInstagram size={32} />,
      label: "Instagram",
      color: "hover:text-pink-500",
    },
    {
      href: null,
      icon: <FaFacebookSquare size={32} />,
      label: "Facebook",
      color: "hover:text-blue-600",
      disabled: true,
    },
    {
      href: "https://t.me/Algoritm_Academy",
      icon: <FaTelegramPlane size={32} />,
      label: "Telegram",
      color: "hover:text-sky-500",
    },
  ];

  const navLinks = [
    { href: "#home", label: "Bosh sahifa" },
    { href: "#about", label: "O‘quv markaz haqida" },
    { href: "#teachers", label: "O‘qituvchilar" },
    { href: "#events", label: "Tadbirlar" },
    { href: "#comments", label: "Fikrlar" },
  ];

  return (
    <motion.footer
      ref={ref}
      initial={{ opacity: 0, y: 100 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, ease: "easeOut" }}
      className="footer bg-gradient-to-t from-gray-900 to-gray-800 text-white"
      role="contentinfo"
      aria-labelledby="footer-title"
    >
      <div className="container footer-container">
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="footer-brand"
        >
          <a
            href="#home"
            onClick={(e) => scrollToSection(e, "#home")}
            aria-label="Bosh sahifaga o'tish"
          >
            <img
              src={logo}
              alt="Algoritm o'quv markazi logotipi"
              className="footer-logo"
              loading="lazy"
              width="180"
              height="60"
            />
          </a>
          <p className="footer-description mt-4 text-gray-300">
            Kelajakni bugundan boshlab quramiz. Algoritm — zamonaviy ta'lim
            markazi.
          </p>
        </motion.div>

        {/* Tezkor havolalar */}
        <div className="footer-nav-wrapper">
          <p className="nav-title" id="quick-links-title">
            Tezkor havolalar
          </p>
          <nav aria-labelledby="quick-links-title">
            <ul className="footer-links space-y-3">
              {navLinks.map((link, index) => (
                <motion.li
                  key={link.href}
                  initial={{ opacity: 0, x: -30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 + 0.3 }}
                >
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="footer-link inline-block transition-all duration-300 hover:text-orange-400 hover:translate-x-2"
                  >
                    {link.label}
                  </a>
                </motion.li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="footer-nav-wrapper">
          <p className="nav-title">Biz bilan bog‘laning</p>
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 }}
              className="flex items-center gap-3"
            >
              <span className="text-orange-400 text-xl">Telefon: </span>
              <a
                href="tel:+998908950505"
                className="phone-number text-lg font-semibold hover:text-orange-400 transition"
              >
                +998 90-895-05-05
              </a>
            </motion.div>
          </div>
        </div>

        <div className="footer-socials" aria-label="Ijtimoiy tarmoqlar">
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.6 }}
            className="social-title"
          >
            Bizni kuzating:
          </motion.p>
          <div className="social-icons">
            {socialLinks.map((social, index) => (
              <motion.a
                key={social.label}
                href={social.disabled ? "#" : social.href}
                target={social.disabled ? undefined : "_blank"}
                rel={social.disabled ? undefined : "noopener noreferrer"}
                className={`social-link ${
                  social.disabled
                    ? "text-gray-500 cursor-not-allowed"
                    : social.color
                }`}
                aria-label={
                  social.disabled
                    ? `${social.label} (tez orada)`
                    : `${social.label} sahifasiga o'tish`
                }
                title={
                  social.disabled ? `${social.label} (tez orada)` : social.label
                }
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.7 + index * 0.12 }}
                whileHover={
                  social.disabled
                    ? {}
                    : { scale: 1.3, rotate: [0, -15, 15, 0], y: -8 }
                }
                whileTap={social.disabled ? {} : { scale: 0.9 }}
                onClick={(e) => social.disabled && e.preventDefault()}
              >
                {social.icon}
              </motion.a>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.9 }}
          className="footer-bottom col-span-full text-center pt-8 border-t border-gray-700 mt-10"
        >
          <p className="copyright text-sm text-gray-400">
            © {currentYear} <strong>Algoritm o‘quv markazi</strong>. Barcha
            huquqlar himoyalangan.
          </p>
          <p className="developed-by text-xs text-gray-500 mt-2">
            Dizayn va dasturlash —{" "}
            <strong className="text-orange-400">Algoritm Team</strong>
          </p>
        </motion.div>
      </div>
    </motion.footer>
  );
});

Footer.displayName = "Footer";
export default Footer;
